import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader } from '../components/ui/card';
import { toast } from 'sonner';
import { LegalFooter } from '../components/LegalFooter';
import { Shield } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // 2FA State
  const [requires2FA, setRequires2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password, rememberMe);

      // Passwort-Änderungspflicht beim ersten Login
      if (user.must_change_password) {
        navigate('/first-login-password-change');
        return;
      }
      
      // Redirect based on role
      if (user.role === 'superadmin') {
        navigate('/superadmin');
      } else if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'stationsleitung' || user.role === 'employee') {
        navigate('/employee');
      } else {
        navigate('/employee');
      }
      
      // Show success toast AFTER navigation (only on actual login)
      setTimeout(() => {
        toast.success('Erfolgreich angemeldet!', {
          duration: 1500,
        });
      }, 300);
      
    } catch (error) {
      const detail = error.response?.data?.detail;
      
      // Check if 2FA is required
      if (detail === '2FA_REQUIRED' || error.response?.headers?.['x-require-2fa']) {
        setRequires2FA(true);
        setLoading(false);
        return;
      }
      
      // Handle rate limiting
      if (error.response?.status === 429) {
        toast.error(detail || 'Zu viele Anmeldeversuche. Bitte warten Sie.');
      } else {
        toast.error(detail || 'Anmeldung fehlgeschlagen');
      }
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error('Bitte geben Sie einen 6-stelligen Code ein');
      return;
    }
    
    setLoading(true);

    try {
      // Try login with 2FA code
      const user = await login(email, password, rememberMe, twoFACode);

      // Passwort-Änderungspflicht
      if (user.must_change_password) {
        navigate('/first-login-password-change');
        return;
      }

      // Redirect based on role
      if (user.role === 'superadmin') {
        navigate('/superadmin');
      } else if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'stationsleitung' || user.role === 'employee') {
        navigate('/employee');
      } else {
        navigate('/employee');
      }
      
      setTimeout(() => {
        toast.success('Erfolgreich angemeldet!', {
          duration: 1500,
        });
      }, 300);
      
    } catch (error) {
      const detail = error.response?.data?.detail;
      
      if (error.response?.status === 429) {
        toast.error('Zu viele Anmeldeversuche. Bitte warten Sie.');
      } else if (error.response?.status === 400 || error.response?.status === 403) {
        toast.error('Ungültiger Code. Bitte versuchen Sie es erneut.');
      } else {
        toast.error(detail || 'Anmeldung fehlgeschlagen');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setTwoFACode('');
  };

  return (
    <div
      className="flex items-start sm:items-center justify-center p-4 relative overflow-y-auto"
      style={{
        minHeight: '100dvh',   /* dvh = Dynamic Viewport Height, berücksichtigt iOS Tastatur */
        paddingTop: 'max(16px, env(safe-area-inset-top, 16px))',
        paddingBottom: 'max(80px, env(safe-area-inset-bottom, 80px))',  /* Platz für Autofill-Bar */
        backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.85)), url('https://images.unsplash.com/photo-1632671414008-d3ea7df1dc4d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBnYXMlMjBzdGF0aW9uJTIwZXh0ZXJpb3IlMjBuaWdodHxlbnwwfHx8fDE3NjU4MzM3Nzh8MA&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <img src="/allflowx-horizontal-light.png" alt="AllFlowX" className="h-24 w-auto object-contain dark:hidden" />
            <img src="/allflowx-horizontal-dark.png" alt="AllFlowX" className="h-24 w-auto object-contain hidden dark:block" />
          </div>
          <CardDescription className="text-center">
            {requires2FA ? (
              <div className="flex items-center justify-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <span>Zwei-Faktor-Authentifizierung</span>
              </div>
            ) : (
              'Melden Sie sich bei Ihrem Konto an'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!requires2FA ? (
            // Step 1: Email + Password
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
              <Input
                id="email"
                type="email"
                placeholder="ihre@email.de"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                style={{ fontSize: '16px' }}   /* iOS: verhindert Auto-Zoom */
                autoComplete="email"
                autoCapitalize="none"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Passwort</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-xs text-primary hover:underline"
                >
                  Passwort vergessen?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                style={{ fontSize: '16px' }}   /* iOS: verhindert Auto-Zoom */
                autoComplete="current-password"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="rememberMe"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <Label htmlFor="rememberMe" className="text-sm font-normal cursor-pointer">
                Angemeldet bleiben
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Anmelden...' : 'Anmelden'}
            </Button>
          </form>
          ) : (
            // Step 2: 2FA Code
            <form onSubmit={handle2FASubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="twofa-code">Authenticator-Code</Label>
                <Input
                  id="twofa-code"
                  type="text"
                  placeholder="000000"
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="text-center text-lg tracking-widest font-mono"
                  required
                  autoFocus
                />
                <p className="text-xs text-muted-foreground text-center">
                  Geben Sie den 6-stelligen Code aus Ihrer Authenticator-App ein
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={handleBack}
                  disabled={loading}
                >
                  Zurück
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={loading || twoFACode.length !== 6}
                >
                  {loading ? 'Wird überprüft...' : 'Anmelden'}
                </Button>
              </div>
            </form>
          )}
          
          {!requires2FA && (
            <div className="mt-4 text-center text-sm">
              <span className="text-muted-foreground">Noch kein Konto? </span>
              <Link to="/register" className="text-primary hover:underline" data-testid="register-link">
                Jetzt registrieren
              </Link>
            </div>
          )}

          {/* Legal Footer */}
          <LegalFooter />
        </CardContent>
      </Card>
    </div>
  );
};
